import React, { useEffect, useState, useRef, useCallback } from "react";
import API from "../../../services";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "./Notifications.scss";  
import { BannerNavigation } from "../../../store/authActions";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Loader } from "../../../constants/whitelabelEnv";
import { domain } from "../../../constants/Branding";

interface WebNotificationItem {
  key: string;
  message: string;
  link: string;
  status: string;
}

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<WebNotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const navigate = useNavigate();
  const [pageToken, setPageToken] = useState<string>("");
  const [hasMore, setHasMore] = useState(true);
  const [filterStatus, setFilterStatus] = useState<"ALL" | "READ" | "UNREAD">("ALL");
  const observerTarget = useRef<HTMLDivElement>(null);

  const getNotifications = useCallback(async (isLoadMore: boolean = false, currentPageToken: string = "", currentFilter: string = "ALL") => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    let response: any;
    try {
      const statusParam = currentFilter === "ALL" ? "" : currentFilter;
      response = await API.post(`/users/get-web-notifications`, {
        page: 1,
        pageSize: 20,
        status: statusParam,
        pageToken: isLoadMore ? currentPageToken : "",
        userKey: sessionStorage.getItem("userKey"),
      });
    } catch (e) {
      console.error("Error fetching notifications:", e);
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
      return;
    }

    if (response.data.status !== "RS_OK") {
      if (!isLoadMore) {
        toast.error(response.data.errDescription || "Failed to fetch notifications");
      }
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
      return;
    }

    if (response && response.data?.notifications) {
      const newNotifications = response.data.notifications;
      if (isLoadMore) {
        setNotifications((prev) => [...prev, ...newNotifications]);
      } else {
        setNotifications(newNotifications);
      }

      setPageToken(response.data.pageToken || "");
      setHasMore(newNotifications.length === 20 && response.data.pageToken);
    } else {
      if (!isLoadMore) {
        setNotifications([]);
      }
      setHasMore(false);
    }

    if (isLoadMore) {
      setLoadingMore(false);
    } else {
      setLoading(false);
    }
  }, []);

  const markAsRead = async (notificationKey: string) => {
    let response: any;
    try {
      response = await API.post(`/users/update-web-notification-status`, {
        key: notificationKey,
        status: "READ",
      });
    } catch (e) {
      console.error("Error marking notification as read:", e);
      toast.error("Failed to mark notification as read");
      return;
    }

    if (response.data.status !== "RS_OK") {
      toast.error(response.data.errDescription || "Failed to mark notification as read");
      return;
    }

    // Update notification status in the list
    setNotifications((prev) => {
      const updated = prev.map((notif) =>
        notif.key === notificationKey ? { ...notif, status: "READ" } : notif
      );

      // Only remove from list if filtering by UNREAD
      // If filterStatus is "ALL" or "READ", keep it in the list with updated status
      if (filterStatus === "UNREAD") {
        return updated.filter((notif) => notif.key !== notificationKey);
      }

      return updated;
    });

    toast.success("Notification marked as read");
  };

  useEffect(() => {
    setPageToken("");
    setHasMore(true);
    setNotifications([]);
    getNotifications(false, "", filterStatus);
  }, [filterStatus, getNotifications]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          getNotifications(true, pageToken, filterStatus);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loadingMore, loading, pageToken, filterStatus, getNotifications]);

  const handleNotificationClick = (link: string) => {
    if (link) {
      if (link.startsWith("http")) {
        window.open(link, "_blank");
      } else {
        BannerNavigation(link, navigate);
      }
    }
  };

  const handleBackClick = () => {
    navigate(-1);
};


  return (
    <div className="flex flex-col px-3 mt-4 w-full pb-[7rem] py-[4rem] notifications-page">
      <header className="flex gap-3 items-center self-start text-base font-medium text-center text-white mb-4 w-full">
        <div>
          <div className="bg-inputbox-color flex p-1 mr-[0.25rem] rounded"
            onClick={() => handleBackClick()}
          >
            <ArrowBackIcon
              className="text-border-color aspect-square w-[18px]"
              style={{ width: '20px', height: '20px' }}
            />
          </div>
        </div>
        <h2 className="self-stretch my-auto">Notifications</h2>
      </header>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-4 w-full">
        <button
          onClick={() => setFilterStatus("ALL")}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition ${filterStatus === "ALL"
              ? "bg-border-color text-white"
              : "bg-inputbox-color text-zinc-400 hover:text-white"
            }`}
        >
          All
        </button>
        <button
          onClick={() => setFilterStatus("READ")}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition ${filterStatus === "READ"
              ? "bg-border-color text-white"
              : "bg-inputbox-color text-zinc-400 hover:text-white"
            }`}
        >
          Read
        </button>
        <button
          onClick={() => setFilterStatus("UNREAD")}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition ${filterStatus === "UNREAD"
              ? "bg-border-color text-white"
              : "bg-inputbox-color text-zinc-400 hover:text-white"
            }`}
        >
          Unread
        </button>
      </div>

      {loading ? (
        <div className="home-loading">
          <img src={Loader(domain)} style={{ height: "10rem" }} alt="Loading" />
        </div>
      ) : (
        <>
          {notifications.length === 0 ? (
            <div className="flex flex-col justify-center items-center py-12 text-white">
              <div className="text-lg mb-2">🔔</div>
              <div className="text-sm text-zinc-400">No notifications available</div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {notifications.map((notification) => (
                <div
                  key={notification.key}
                  className={`notification-card flex flex-col px-4 py-3 rounded-lg border border-solid transition-all ${notification.status === "UNREAD"
                      ? "bg-inputbox-color border-blue-500/50 shadow-lg shadow-blue-500/10"
                      : "bg-inputbox-color border-slate-800 opacity-75"
                    }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                      {notification.status === "UNREAD" && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                          <span className="text-xs text-blue-400 font-medium">New</span>
                        </div>
                      )}
                      <p
                        className={`text-sm leading-relaxed ${notification.status === "UNREAD"
                            ? "text-white font-medium"
                            : "text-zinc-300"
                          }`}
                        onClick={() => notification.link && handleNotificationClick(notification.link)}
                        style={notification.link ? { cursor: "pointer" } : {}}
                      >
                        {notification.message}
                      </p>
                    </div>
                  </div>
                  {(notification.link || notification.status === "UNREAD") && (
                    <div className="flex items-center justify-between pt-2 border-t border-slate-700/50">
                      {notification.link && (
                        <button
                          onClick={() => handleNotificationClick(notification.link)}
                          className="text-xs text-blue-400 hover:text-blue-300 transition font-medium"
                        >
                          → Click Here
                        </button>
                      )}
                      <div className="ml-auto">
                        {notification.status === "UNREAD" && (
                          <button
                            onClick={() => markAsRead(notification.key)}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition"
                          >
                            Mark as Read
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {/* Infinite scroll trigger */}
              <div ref={observerTarget} className="h-4 w-full">
                {loadingMore && (
                  <div className="flex justify-center py-4">
                    <img src={Loader(domain)} style={{ height: "3rem" }} alt="Loading more" />
                  </div>
                )}
              </div>
              {!hasMore && notifications.length > 0 && (
                <div className="text-center py-4 text-zinc-500 text-sm">
                  No more notifications
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Notifications;
